import * as yup from 'yup';

/**
 * E47 · Schema modes — the both-ends input codecs of the four mode mutations (plan 03 §2.1 #3): the phone (before it saves or
 * queues), the portal and — as the shield's inputRule — the API run the SAME schema. SHAPE only; the business rules (a set's
 * size and its Healthy Adult, an own mode's kind, a mode that is not in the set, an intensity outside 0–10, a moment too far back,
 * a card that is not the caller's) are the deciders', where they are binding and answer with the board's coded refusals in the
 * person's language. `performer` is ABSENT on purpose: the caller is the performer.
 */
export const MODE_KIND_VALUES = [
  'child',
  'coping',
  'critic',
  'healthyAdult',
  'happyChild',
] as const;

export const MODE_NEED_VALUES = [
  'safety',
  'connection',
  'acceptance',
  'expression',
  'autonomy',
  'play',
  'limits',
] as const;

const uuidV7 = yup
  .string()
  .trim()
  .matches(
    /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
  )
  .required();

const userRef = yup
  .string()
  .trim()
  .matches(/^users\/[A-Za-z0-9_-]{1,64}$/);

const modeId = yup
  .string()
  .trim()
  .matches(/^[A-Za-z0-9_-]{1,24}$/)
  .required();

export const modeInputSchema = yup.object({
  modeId,
  kind: yup
    .string()
    .oneOf([...MODE_KIND_VALUES])
    .required(),
  // the person's words: a name is shape (a blank one is not a mode); the 60 is generous, a mode name is a few words
  label: yup.string().trim().min(1).max(60).required(),
  colour: yup
    .string()
    .trim()
    .matches(/^#[0-9A-Fa-f]{6}$/)
    .nullable(),
  own: yup.boolean().nullable(),
});

export const configureModeSetInputSchema = yup.object({
  subject: userRef.nullable(),
  // no `min(1)`: an empty set is the decider's coded refusal (MODE_SET_EMPTY)
  modes: yup.array().of(modeInputSchema.required()).max(40).required(),
});

export const recordModeCheckInInputSchema = yup.object({
  checkInId: uuidV7,
  // the intensity is a whole number here; whether it is 0–10 is the decider's (MODE_INTENSITY_RANGE)
  modes: yup
    .array()
    .of(
      yup
        .object({ modeId, intensity: yup.number().integer().required() })
        .required(),
    )
    .max(40)
    .required(),
  trigger: yup.string().max(1000).nullable(),
  bodyCue: yup.string().max(1000).nullable(),
  need: yup
    .string()
    .oneOf([...MODE_NEED_VALUES])
    .nullable(),
  note: yup.string().max(4000).nullable(),
  recordedAt: yup.date().required(),
  occurredAt: yup.date().nullable(),
});

export const linkCardToModeInputSchema = yup.object({
  modeId,
  cardId: yup.string().trim().min(1).max(128).nullable(),
});

export const deleteModeCheckInInputSchema = yup.object({
  checkInId: yup.string().trim().min(1).max(128).required(),
});

/** The mutation ARGUMENTS — what the shield's inputRules validate. */
export const configureModeSetArgsSchema = yup.object({
  input: configureModeSetInputSchema.required(),
});
export const recordModeCheckInArgsSchema = yup.object({
  input: recordModeCheckInInputSchema.required(),
});
export const linkCardToModeArgsSchema = yup.object({
  input: linkCardToModeInputSchema.required(),
});
export const deleteModeCheckInArgsSchema = yup.object({
  input: deleteModeCheckInInputSchema.required(),
});

// Typed loosely on purpose (TS7056 — see diaryCard.ts): the shape is the Yup schemas above.
export const modeValidators: Record<
  | 'configureModeSetInputSchema'
  | 'recordModeCheckInInputSchema'
  | 'linkCardToModeInputSchema'
  | 'deleteModeCheckInInputSchema'
  | 'configureModeSetArgsSchema'
  | 'recordModeCheckInArgsSchema'
  | 'linkCardToModeArgsSchema'
  | 'deleteModeCheckInArgsSchema',
  yup.AnySchema
> = {
  configureModeSetInputSchema,
  recordModeCheckInInputSchema,
  linkCardToModeInputSchema,
  deleteModeCheckInInputSchema,
  configureModeSetArgsSchema,
  recordModeCheckInArgsSchema,
  linkCardToModeArgsSchema,
  deleteModeCheckInArgsSchema,
};
